import { useQuery } from '@tanstack/react-query';
import type {
  MeetingPageDebrief,
  MeetingPageRecap,
  MeetingPrepIntake,
} from '@wundo/shared/meetingPage.ts';
import { formatDayAndMonth } from '@/components/agenda/meetingFormat.ts';
import { meetingPageTitle } from '@/components/meetings/meetingPageTitle.ts';
import type { MeetingPanel } from '@/components/meetings/MeetingView.tsx';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import {
  type ChatPageNudge,
  type ChatPageOffer,
  usePublishChatPageOffer,
} from '@/lib/chatPageOffer.ts';
import { useChatPageNudge } from '@/lib/useChatPageNudge.ts';
import { fetchMeetingPage } from '@/lib/meetingPage.ts';
import { m } from '@/paraglide/messages.js';

/**
 * What the chat offers beside a meeting, which is not the same thing on both of
 * its tabs: preparing a conversation and writing it up are two jobs, and the
 * help worth reaching for differs. The lead action is the one this tab is
 * really for.
 *
 * "What was said last time" is one of them, and only when there was a last
 * time. It used to be a card at the top of the preparation, which put a
 * paragraph nobody asked for above the document they came to write — in the
 * chat it is one click for whoever wants it, answered from the same verslag.
 */
function meetingChatOffer(
  panel: MeetingPanel,
  meeting: string | null,
  recap: MeetingPageRecap | null,
  intake: MeetingPrepIntake | null,
  debrief: MeetingPageDebrief | null,
  hasTranscript: boolean,
  meetingId: string,
  nudge: ChatPageNudge | undefined,
): ChatPageOffer {
  const hasRecap = recap !== null;

  return {
    /*
     * The one thing worth recommending about this conversation to somebody
     * still finding their way (DEV-72). It ranks behind both openers, so a
     * meeting Wundo still has questions about never shows one.
     */
    ...(nudge ? { nudge } : {}),
    /* Until the meeting has a name there is nothing to name it after. */
    line:
      meeting === null
        ? m.chat_line_meeting()
        : panel === 'prep'
          ? m.chat_line_meeting_prep({ meeting })
          : m.chat_line_meeting_report({ meeting }),
    quickActions:
      panel === 'prep'
        ? [
            /*
             * On a meeting Wundo cannot prepare on its own this is what the tab
             * is for, so it leads. It sends nothing: it stands the drawer where
             * this meeting's opener belongs — the conversation it was already
             * answered in, or a blank one for the question itself — which is where
             * the drawer already is unless the user went reading something else,
             * and hands the caret over to answer in their own words.
             */
            ...(intake
              ? [
                  {
                    text: m.chat_quick_prep_intake(),
                    icon: 'prepare' as const,
                    behavior: 'intake' as const,
                  },
                ]
              : []),
            { text: m.chat_quick_prep_tips(), icon: 'tips' },
            { text: m.chat_quick_prep_roleplay(), icon: 'roleplay' },
            ...(hasRecap
              ? [{ text: m.chat_quick_prep_recap(), icon: 'recap' as const }]
              : []),
          ]
        : /*
           * On a meeting Wundo has nothing to write the verslag from this is what
           * the tab is for, so it is the only thing offered — it sends nothing and
           * stands the drawer where this meeting's opener belongs, the way the
           * preparation's own opener does. Feedback needs a transcript to be
           * feedback about, so without one there is nothing worth offering here.
           */
          debrief
          ? [
              {
                text: m.chat_quick_report_debrief(),
                icon: 'recap' as const,
                behavior: 'intake' as const,
              },
            ]
          : hasTranscript
            ? [{ text: m.chat_quick_report_check(), icon: 'check' as const }]
            : [],
    /*
     * Only on the preparation: the opener asks about the conversation to come,
     * and the verslag tab is about the one that already happened.
     */
    ...(intake && panel === 'prep'
      ? {
          intake: {
            opener: intake.opener,
            meetingId,
            /*
             * Where the answering is already happening, as the server sees it —
             * so a page reloaded halfway through hands the drawer that
             * conversation instead of the opening question a second time.
             */
            conversationId: intake.conversation_id,
          },
        }
      : {}),
    /*
     * And only on the verslag, for the mirror of that reason: the debrief asks
     * how the conversation went, which is a question about a sitting that has
     * already happened.
     */
    ...(debrief && panel === 'report'
      ? {
          debrief: {
            opener: debrief.opener,
            meetingId,
            /* Where the answering is already happening, as the server sees it. */
            conversationId: debrief.conversation_id,
          },
        }
      : {}),
    /*
     * And on the preparation only, for the same reason: what was said last time
     * is what somebody preparing the next conversation wants in front of them,
     * and the verslag tab is about the sitting that already happened.
     *
     * The date is written here rather than by the server — it is the reader's
     * own locale, the same "28 mei" the agenda writes, and the words around it
     * never leave the browser.
     */
    ...(recap && panel === 'prep'
      ? {
          recap: {
            text: recap.text,
            meetingId,
            title: m.chat_recap_card_title({
              date: formatDayAndMonth(new Date(recap.meeting_starts_at)),
            }),
            linkText: m.chat_recap_card_link({
              date: formatDayAndMonth(new Date(recap.meeting_starts_at)),
            }),
            linkMeetingId: recap.meeting_id,
            /* Where it has already been taken up, as the server sees it. */
            conversationId: recap.conversation_id,
          },
        }
      : {}),
  };
}

/**
 * Publish what this meeting offers the drawer, for as long as the page is up.
 *
 * The three things that can stand in a blank drawer arrive by two different
 * roads: the opener and the summary come with the meeting's own page, and the
 * nudge comes from its own, much faster request. The drawer shows one of them
 * and ranks the nudge last — so a nudge threaded in while the page is still
 * loading would take a slot the summary is about to claim, and the user would
 * watch one card replace the other.
 *
 * Hence the gate: the nudge joins the offer only once the meeting's page has
 * settled and it is known whether there is an opener or a summary at all.
 * Until then the offer is published without one. Nothing is lost by waiting —
 * the nudge is about the person, not about this page, and a moment late is not
 * late.
 */
export function useMeetingChatOffer(meetingId: string, panel: MeetingPanel): void {
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  /*
   * The chat's line needs the meeting's name, and the view is already reading
   * it under this key — so this is the cache, not a second request.
   */
  const { data: page, isPending: pageUnsettled } = useQuery({
    queryKey: ['meetingPage', user?.id, meetingId],
    queryFn: () => fetchMeetingPage(user!.id, meetingId),
    enabled: Boolean(user?.id),
  });

  /*
   * Which nudge, if any, belongs beside this tab — the two meeting-scoped ones
   * are about the conversation on screen, so the tab is part of the question.
   */
  const nudge = useChatPageNudge(
    panel === 'prep'
      ? { page: 'meeting-prep', meeting_id: meetingId }
      : { page: 'meeting-report', meeting_id: meetingId },
  );

  usePublishChatPageOffer(
    meetingChatOffer(
      panel,
      page ? meetingPageTitle(page) : null,
      page?.recap ?? null,
      page?.prep_intake ?? null,
      page?.debrief ?? null,
      /*
       * Feedback about how the conversation went is only worth offering where
       * Wundo listened in: a verslag written from a pasted note is the user's
       * own words back, and there is nothing to hold it against.
       */
      page?.materials.some((material) => material.kind === 'transcript') ?? false,
      meetingId,
      pageUnsettled ? undefined : nudge,
    ),
  );
}
